import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Shield,
  Eye,
  Volume2,
  Video,
  Battery,
  Moon,
  WifiOff,
  Music,
  SwitchCamera,
  Smartphone,
  CheckCircle2,
  Clock,
  Calendar,
} from "lucide-react";
import { CookieBanner } from "../components/CookieBanner";
import styles from "./BlogIphoneBabyMonitorPage.module.scss";

// JSON-LD Article Schema
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "How to Turn Your iPhone into a Secure Baby Monitor (Free)",
  description:
    "Complete guide to repurposing your iPhone or iPad into a free, encrypted baby monitor using GoodBaby. No app installation required.",
  image:
    "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor/og-image.png",
  author: {
    "@type": "Person",
    name: "Nakul Rathore",
    url: "https://goodbaby.cam",
  },
  publisher: {
    "@type": "Organization",
    name: "GoodBaby",
    logo: {
      "@type": "ImageObject",
      url: "https://goodbaby.cam/logo.png",
    },
  },
  datePublished: "2026-01-15",
  dateModified: "2026-01-28",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id":
      "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor",
  },
};

// JSON-LD HowTo Schema
const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Turn Your iPhone into a Baby Monitor",
  description:
    "Step-by-step guide to set up a free, secure baby monitor using your iPhone and GoodBaby.",
  totalTime: "PT5M",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "USD",
    value: "0",
  },
  supply: [
    {
      "@type": "HowToSupply",
      name: "iPhone or iPad",
    },
    {
      "@type": "HowToSupply",
      name: "Phone tripod or mounting solution",
    },
  ],
  tool: [
    {
      "@type": "HowToTool",
      name: "Safari browser",
    },
  ],
  step: [
    {
      "@type": "HowToStep",
      name: "Prepare the Baby Device",
      text: "Connect to WiFi and enable Do Not Disturb. GoodBaby handles screen brightness and auto-lock automatically.",
      url: "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor#step-1",
    },
    {
      "@type": "HowToStep",
      name: "Start the Baby Stream",
      text: "Open Safari, go to goodbaby.cam, tap Get Started, select Baby Mode, and allow camera/microphone access.",
      url: "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor#step-2",
    },
    {
      "@type": "HowToStep",
      name: "Connect the Parent Device",
      text: "On your current phone, go to goodbaby.cam, select Parent Mode, and pair with the baby device.",
      url: "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor#step-3",
    },
  ],
};

// FAQ Schema
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is GoodBaby really free?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, 100% free. No monthly fees, no premium tier for essential features, no ads. We believe peace of mind shouldn't come with a subscription fee.",
      },
    },
    {
      "@type": "Question",
      name: "Does GoodBaby work on 3G/4G/5G mobile data?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. While WiFi provides the best latency, GoodBaby works seamlessly over mobile data. Perfect for travel, hotels with spotty WiFi, or checking in from work.",
      },
    },
    {
      "@type": "Question",
      name: "What happens if the internet goes down?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "GoodBaby constantly monitors connection heartbeats between devices. If the baby unit stops responding for any reason—power outage, internet loss, accidental app closure—the parent unit will sound an alarm within seconds.",
      },
    },
    {
      "@type": "Question",
      name: "Can I use GoodBaby with pets?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Of course! GoodBaby works great as a pet camera. Many users monitor their dogs, cats, and other pets using the same setup.",
      },
    },
    {
      "@type": "Question",
      name: "Does my iPhone work in the dark?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Modern smartphones have increasingly good low-light cameras. The image won't be as crisp as daytime, but you'll see enough to check on your baby. Some parents add a dim red nightlight to improve visibility.",
      },
    },
  ],
};

// Breadcrumb Schema
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://goodbaby.cam",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Blog",
      item: "https://goodbaby.cam/",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "iPhone Baby Monitor Guide",
      item: "https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor",
    },
  ],
};

export function BlogIphoneBabyMonitorPage() {
  return (
    <div className={styles.page}>
      <Helmet>
        <title>
          How to Turn Your iPhone into a Baby Monitor (Free Guide 2026) |
          GoodBaby
        </title>
        <meta
          name="description"
          content="Complete step-by-step guide to turn your iPhone or iPad into a free, secure baby monitor. No apps to install, no monthly fees. Uses Industry-level encryption."
        />
        <meta
          name="keywords"
          content="iphone baby monitor, iPhone baby monitor, diy baby monitor, free baby monitor app, use old phone as baby monitor, baby monitor no subscription, secure baby monitor, p2p baby monitor"
        />
        <link
          rel="canonical"
          href="https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor"
        />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor"
        />
        <meta
          property="og:title"
          content="How to Turn Your iPhone into a Baby Monitor (Free Guide)"
        />
        <meta
          property="og:description"
          content="Turn your iPhone into a secure, free baby monitor in 5 minutes. No apps, no subscriptions, Industry-level encryption."
        />
        <meta
          property="og:image"
          content="https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor/og-image.png"
        />
        <meta property="article:published_time" content="2026-01-15" />
        <meta property="article:modified_time" content="2026-01-28" />
        <meta property="article:author" content="Nakul Rathore" />
        <meta property="article:section" content="Guides" />
        <meta property="article:tag" content="Baby Monitor" />
        <meta property="article:tag" content="iPhone" />
        <meta property="article:tag" content="DIY" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="How to Turn Your iPhone into a Baby Monitor (Free)"
        />
        <meta
          name="twitter:description"
          content="Turn your iPhone into a secure, free baby monitor in 5 minutes. No apps, no subscriptions."
        />
        <meta
          name="twitter:image"
          content="https://goodbaby.cam/blog/how-to-use-existing-iphone-as-baby-monitor/og-image.png"
        />

        {/* JSON-LD Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>Blog</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>
          iPhone Baby Monitor Guide
        </span>
      </nav>

      {/* Article */}
      <article className={styles.article}>
        {/* Hero */}
        <header className={styles.articleHeader}>
          <div className={styles.articleMeta}>
            <span className={styles.category}>Guide</span>
            <span className={styles.readTime}>
              <Clock size={14} />3 min read
            </span>
          </div>

          <h1 className={styles.articleTitle}>
            How to Turn Your iPhone into a Secure Baby Monitor
            <span className={styles.titleHighlight}> (For Free)</span>
          </h1>

          <p className={styles.articleExcerpt}>
            Complete step-by-step guide to repurposing your iPhone or iPad into
            a HD baby monitor with motion alerts, and industry-level encryption,
            without spending a dime.
          </p>

          <div className={styles.authorInfo}>
            <div className={styles.authorDetails}>
              <span className={styles.authorName}>Nakul Rathore</span>
              <span className={styles.publishDate}>
                <Calendar size={14} />
                Updated January 2026
              </span>
            </div>
          </div>
        </header>

        {/* Hero Image Placeholder */}
        <div className={styles.heroImage}>
          <div className={styles.imagePlaceholder}>
            <Smartphone size={48} />
            <span>
              Hero Image: iPhone showing GoodBaby baby monitor interface
            </span>
          </div>
        </div>

        {/* Table of Contents */}
        <nav className={styles.tableOfContents}>
          <h2>In This Guide</h2>
          <ol>
            <li>
              <a href="#why-use-iphone">
                Why Use Your iPhone as a Baby Monitor?
              </a>
            </li>
            <li>
              <a href="#features">Complete GoodBaby Feature List</a>
            </li>
            <li>
              <a href="#setup-guide">Step-by-Step Setup Guide</a>
            </li>
            <li>
              <a href="#pro-tips">Pro Tips for Best Results</a>
            </li>
            <li>
              <a href="#comparison">GoodBaby vs Traditional Monitors</a>
            </li>
            <li>
              <a href="#faq">Frequently Asked Questions</a>
            </li>
            <li>
              <a href="#troubleshooting">Troubleshooting</a>
            </li>
          </ol>
        </nav>

        {/* Content */}
        <div className={styles.articleContent}>
          {/* Introduction */}
          <p className={styles.leadParagraph}>
            If you're a new parent, you likely have two things in abundance: a
            baby that needs constant watching, and a drawer somewhere full of
            old electronics. Specifically, perfectly good iPhones gathering
            dust—an iPhone 6, an iPhone 7, or maybe an iPad you haven't touched
            in years.
          </p>

          <p>
            What if that old device could become one of the most valuable tools
            in your parenting arsenal?
          </p>

          <p>
            For years, the standard advice for a "DIY baby monitor" was to
            download a sketchy app from the App Store. But if you've tried this
            recently, you know the frustration all too well: the app only works
            on iOS 15+ (which your iPhone 6 can't run), it demands a monthly
            subscription of $5-15 just for the privilege of using your own
            camera, or—worst of all—it forces you to stream video through some
            unknown cloud server.
          </p>

          <div className={styles.calloutBox}>
            <strong>The Good News:</strong> There's a better, safer way. You
            don't need the App Store. You don't need a subscription. You just
            need the device you already own and the browser already installed on
            it.
          </div>

          {/* Inline CTA 1 */}
          <div className={styles.inlineCta}>
            <p>Ready to get started?</p>
            <Link to="/signup" className={styles.inlineCtaButton}>
              <span>Try GoodBaby Free</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          {/* Section: Why Use iPhone */}
          <section id="why-use-iphone">
            <h2>Why Use Your iPhone as a Baby Monitor?</h2>

            <p>
              Before we dive into the step-by-step setup, let's address the
              elephant in the room: why would you do this instead of just buying
              a dedicated baby monitor from Amazon?
            </p>

            <h3>Your iPhone Camera is Better Than You Think</h3>

            <p>
              That "outdated" iPhone 6 sitting in your drawer? Its 8-megapixel
              camera with optical image stabilization is actually{" "}
              <strong>superior</strong> to the cameras found in most dedicated
              baby monitors—even the expensive ones.
            </p>

            {/* Screenshot Placeholder */}
            <div className={styles.screenshotPlaceholder}>
              <div className={styles.imagePlaceholder}>
                <Video size={32} />
                <span>
                  Screenshot: Video quality comparison between iPhone 6 and $150
                  baby monitor
                </span>
              </div>
            </div>

            <p>
              Most baby monitors under $200 use cheap VGA or 720p sensors that
              produce grainy, washed-out images. Your iPhone, on the other hand,
              was designed to take professional-quality photos. When it comes to
              seeing your baby clearly in low light, there's really no
              comparison.
            </p>

            <h3>The Privacy Problem with Traditional Baby Monitors</h3>

            <p>
              Here's something that keeps security researchers up at night: many
              popular "smart" baby monitors have{" "}
              <strong>terrible security</strong>. There have been numerous
              documented cases of strangers accessing baby monitors and speaking
              to children through the two-way audio feature.
            </p>

            <p>This happens because these devices often:</p>

            <ul>
              <li>Use weak default passwords that never get changed</li>
              <li>Transmit video over unencrypted connections</li>
              <li>Store footage on cloud servers with questionable security</li>
              <li>Get abandoned by manufacturers who stop releasing updates</li>
            </ul>

            <div className={styles.securityHighlight}>
              <Shield size={24} />
              <div>
                <strong>
                  <Link to="/signup" className={styles.inlineLink}>
                    GoodBaby
                  </Link>{" "}
                  uses WebRTC technology
                </strong>
                —the same encryption that powers Google Meet, Zoom, and
                Microsoft Teams. Your video is end-to-end encrypted and never
                touches our servers.
              </div>
            </div>

            <h3>Unlimited Range—Monitor From Anywhere</h3>

            <p>
              Traditional baby monitors use radio frequency (RF) signals that
              typically give you 700-1000 feet of range—in ideal conditions.
              Step outside to check the mail? The signal cuts out. Walk to the
              kitchen with thick walls? Static.
            </p>

            <p>
              Because{" "}
              <Link to="/signup" className={styles.inlineLink}>
                GoodBaby
              </Link>{" "}
              works over WiFi and cellular data, there's effectively
              <strong> no range limit</strong>. You can monitor from the
              backyard, the gym, a restaurant across town, or even another
              country.
            </p>
          </section>

          {/* Section: Features */}
          <br />
          <br />
          <section id="features">
            <h2>GoodBaby Feature List</h2>
            <p>
              When we built{" "}
              <Link to="/signup" className={styles.inlineLink}>
                GoodBaby
              </Link>
              , we didn't want to create "just another video feed." We wanted
              every feature that expensive monitors have—and some they don't.
            </p>

            <div className={styles.featureGrid}>
              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Video size={24} />
                </div>
                <h4>HD Video Streaming</h4>
                <p>
                  Crystal-clear video using your device's camera—far better than
                  dedicated monitors
                </p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Volume2 size={24} />
                </div>
                <h4>Audio-Only Mode</h4>
                <p>
                  Save battery by streaming audio only—perfect for overnight
                  monitoring when video isn't needed
                </p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Eye size={24} />
                </div>
                <h4>Motion Detection</h4>
                <p>Intelligent alerts when your baby moves or stirs</p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Music size={24} />
                </div>
                <h4>Remote Lullabies</h4>
                <p>
                  Play calming sounds through the baby unit without entering the
                  nursery
                </p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Battery size={24} />
                </div>
                <h4>Battery Monitoring</h4>
                <p>See exact battery percentage and get low battery alerts</p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <Moon size={24} />
                </div>
                <h4>Screen Blinds</h4>
                <p>
                  Remotely black out the baby device screen to keep the room
                  dark
                </p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <WifiOff size={24} />
                </div>
                <h4>Disconnect Alerts</h4>
                <p>Instant alarm if the baby unit loses power or internet</p>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIconBox}>
                  <SwitchCamera size={24} />
                </div>
                <h4>Camera Switching</h4>
                <p>Switch between front and back cameras remotely</p>
              </div>
            </div>
          </section>

          {/* Inline CTA 2 */}
          <div className={styles.inlineCta}>
            <p>All these features, completely free.</p>
            <Link to="/signup" className={styles.inlineCtaButton}>
              <span>Get Started Now</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          {/* Section: Setup Guide */}
          <section id="setup-guide">
            <h2>Step-by-Step Setup Guide</h2>

            <h3>What You'll Need</h3>

            <div className={styles.requirementsBox}>
              <div className={styles.requirementItem}>
                <Smartphone size={20} />
                <div>
                  <strong>Baby Unit</strong>
                  <span>Any iPhone or iPad with a camera</span>
                </div>
              </div>
              <div className={styles.requirementItem}>
                <Smartphone size={20} />
                <div>
                  <strong>Parent Unit</strong>
                  <span>Your current iPhone or iPad</span>
                </div>
              </div>
            </div>

            {/* Step 1 */}
            <div className={styles.step} id="step-1">
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h3>Prepare the Baby Device</h3>
                <p>
                  This step is simple. You're transforming an iPhone into a
                  dedicated baby monitoring appliance.
                </p>

                <ol className={styles.stepList}>
                  <li>
                    <strong>Connect to WiFi</strong> — Make sure it's on a
                    stable network. 5GHz works best if available.
                  </li>
                  <li>
                    <strong>Enable Do Not Disturb</strong> — Critical! You don't
                    want a spam call at 2 AM waking your baby. Go to Settings →
                    Focus → Do Not Disturb.
                  </li>
                </ol>

                <div className={styles.calloutBox}>
                  <strong>GoodBaby Handles the Rest</strong>
                  Don't worry about screen brightness, auto-lock, or keeping
                  Safari open—GoodBaby has built-in features that prevent your
                  device from sleeping during monitoring. Simply use our "Screen
                  Blinds" feature to darken the screen so the glow doesn't
                  disturb your sleeping baby. One tap, and the screen goes
                  completely dark while monitoring continues.
                </div>

                {/* Screenshot Placeholder */}
                <div className={styles.screenshotPlaceholder}>
                  <div className={styles.imagePlaceholder}>
                    <Smartphone size={32} />
                    <span>Screenshot: iPhone Do Not Disturb settings</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className={styles.step} id="step-2">
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h3>Start the Baby Stream</h3>
                <ol className={styles.stepList}>
                  <li>
                    Open <strong>Safari</strong> on your iPhone
                  </li>
                  <li>
                    Navigate to <strong>goodbaby.cam</strong>
                  </li>
                  <li>
                    Tap <strong>"Get Started"</strong> — it's completely free
                  </li>
                  <li>
                    Select <strong>"Baby Mode"</strong>
                  </li>
                  <li>Allow Camera and Microphone access when prompted</li>
                  <li>Position device with clear view of crib</li>
                  <li>
                    Tap <strong>"Start Streaming"</strong>
                  </li>
                </ol>

                {/* Screenshot Placeholder */}
                <div className={styles.screenshotPlaceholder}>
                  <div className={styles.imagePlaceholder}>
                    <Video size={32} />
                    <span>Screenshot: GoodBaby Baby Mode interface</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className={styles.step} id="step-3">
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h3>Connect the Parent Device</h3>
                <ol className={styles.stepList}>
                  <li>
                    On your current iPhone, go to <strong>goodbaby.cam</strong>
                  </li>
                  <li>
                    Tap <strong>"Get Started"</strong> and select{" "}
                    <strong>"Parent Mode"</strong>
                  </li>
                  <li>Your devices will find each other automatically</li>
                  <li>
                    Click on <strong>"Start Monitoring"</strong> button
                  </li>
                  <li>You should now see a live HD video feed of your baby!</li>
                </ol>

                {/* Screenshot Placeholder */}
                <div className={styles.screenshotPlaceholder}>
                  <div className={styles.imagePlaceholder}>
                    <Eye size={32} />
                    <span>
                      Screenshot: GoodBaby Parent Mode with live video feed
                    </span>
                  </div>
                </div>

                <div className={styles.successBox}>
                  <CheckCircle2 size={24} />
                  <div>
                    <strong>That's it!</strong> You're now monitoring your baby
                    with enterprise-grade encryption, HD video, and zero monthly
                    fees.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Inline CTA 3 */}
          <div className={styles.inlineCta}>
            <p>That's all it takes to set up your free baby monitor.</p>
            <Link to="/signup" className={styles.inlineCtaButton}>
              <span>Start Monitoring in Minutes</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          {/* Section: Pro Tips */}
          <section id="pro-tips">
            <h2>Pro Tips for Best Results</h2>

            <h3>Optional: Child-Proofing with Guided Access</h3>
            <p>
              If your baby is a toddler who might reach for the phone, you can
              optionally enable Apple's <strong>Guided Access</strong> feature.
              This locks the device to Safari so curious little fingers can't
              close the app or navigate away.
            </p>

            <ol>
              <li>Settings → Accessibility → Guided Access → Toggle ON</li>
              <li>Set a passcode you'll remember</li>
              <li>Open Safari with GoodBaby running</li>
              <li>Triple-click the side button (or home button)</li>
              <li>Tap "Start"</li>
            </ol>

            <p>
              This is completely optional for most users—only needed if your
              child can physically reach and interact with the baby device.
            </p>

            <h3>Optimal Camera Placement</h3>
            <ul>
              <li>Mount at crib rail height pointing down slightly</li>
              <li>Avoid direct light sources in the frame</li>
              <li>Use the back camera for better quality</li>
              <li>Keep the device plugged in at all times</li>
            </ul>
          </section>

          {/* Section: Comparison */}
          <section id="comparison">
            <h2>GoodBaby vs Baby Monitor Apps & Hardware</h2>

            <p>
              You might be wondering how GoodBaby compares to other baby monitor
              apps from the App Store, or traditional hardware monitors. Here's
              an honest comparison:
            </p>

            <div className={styles.comparisonTable}>
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className={styles.highlightColumn}>GoodBaby</th>
                    <th>Other Apps</th>
                    <th>Hardware Monitors</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Price</td>
                    <td className={styles.highlightColumn}>Free forever</td>
                    <td>$5-15/month</td>
                    <td>$100-250 upfront</td>
                  </tr>
                  <tr>
                    <td>Video Quality</td>
                    <td className={styles.highlightColumn}>HD (your camera)</td>
                    <td>HD (your camera)</td>
                    <td>Usually 720p</td>
                  </tr>
                  <tr>
                    <td>Works on iPhones</td>
                    <td className={styles.highlightColumn}>✓ any iPhone</td>
                    <td>Requires Latest iOS</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td>App Installation</td>
                    <td className={styles.highlightColumn}>
                      None (browser-based)
                    </td>
                    <td>Required</td>
                    <td>Sometimes</td>
                  </tr>
                  <tr>
                    <td>Cloud Storage</td>
                    <td className={styles.highlightColumn}>✓ On Device</td>
                    <td>Yes (privacy risk)</td>
                    <td>Often yes</td>
                  </tr>
                  <tr>
                    <td>Encryption</td>
                    <td className={styles.highlightColumn}>DTLS/SRTP</td>
                    <td>Varies</td>
                    <td>Often weak/none</td>
                  </tr>
                  <tr>
                    <td>Auto Sleep Prevention</td>
                    <td className={styles.highlightColumn}>✓ Built-in</td>
                    <td>Sometimes</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td>Screen Blinds</td>
                    <td className={styles.highlightColumn}>✓ Yes</td>
                    <td>Rare</td>
                    <td>✗</td>
                  </tr>
                  <tr>
                    <td>Motion Alerts</td>
                    <td className={styles.highlightColumn}>✓ Yes</td>
                    <td>Premium tier</td>
                    <td>Some models</td>
                  </tr>
                  <tr>
                    <td>Works Outside Home</td>
                    <td className={styles.highlightColumn}>✓ Anywhere</td>
                    <td>Yes</td>
                    <td>Usually no</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section: FAQ */}
          <section id="faq">
            <h2>Frequently Asked Questions</h2>

            <div className={styles.faqList}>
              <div className={styles.faqItem}>
                <h3>Is GoodBaby really free?</h3>
                <p>
                  Yes, 100% free. No monthly fees, no "premium" tier for
                  essential features, no ads. We believe peace of mind shouldn't
                  come with a subscription fee.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Does it work on 3G/4G/5G?</h3>
                <p>
                  Absolutely. While WiFi provides the best latency, GoodBaby
                  works seamlessly over mobile data. Perfect for travel, hotels
                  with spotty WiFi, or checking in from work.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>What happens if the internet goes down?</h3>
                <p>
                  GoodBaby constantly monitors connection "heartbeats" between
                  devices. If the baby unit stops responding for any
                  reason—power outage, internet loss, accidental app closure—the
                  parent unit will sound an alarm within seconds.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Can I use this with pets too?</h3>
                <p>
                  Of course! GoodBaby works great as a pet camera. Many users
                  monitor their dogs, cats, and other pets using the same setup.
                </p>
              </div>

              <div className={styles.faqItem}>
                <h3>Does it work in the dark?</h3>
                <p>
                  Modern smartphones have increasingly good low-light cameras.
                  The image won't be as crisp as daytime, but you'll see enough
                  to check on your baby. Some parents add a dim red nightlight
                  to improve visibility.
                </p>
              </div>
            </div>
          </section>

          {/* Section: Troubleshooting */}
          <section id="troubleshooting">
            <h2>Troubleshooting Common Issues</h2>

            <div className={styles.troubleshootingList}>
              <div className={styles.troubleshootItem}>
                <h4>"The battery drains too fast"</h4>
                <p>
                  Video encoding is processor-intensive. Always use a wall
                  charger (not a computer USB port), and consider using a
                  higher-wattage charger if available—iPad chargers work great.
                </p>
              </div>

              <div className={styles.troubleshootItem}>
                <h4>"The phone gets really hot"</h4>
                <p>
                  This is normal for continuous video encoding. Ensure good
                  airflow around the device. Don't lay it flat on fabric—prop it
                  up so air can circulate.
                </p>
              </div>

              <div className={styles.troubleshootItem}>
                <h4>"I hear audio feedback (screeching)"</h4>
                <p>
                  This happens when the parent device's speaker is picked up by
                  the baby device's microphone. Keep devices far apart during
                  setup—this won't happen during normal use in different rooms.
                </p>
              </div>

              <div className={styles.troubleshootItem}>
                <h4>"The stream is laggy"</h4>
                <p>
                  Usually a network issue. Try: (1) Move closer to your WiFi
                  router, (2) Switch to 5GHz WiFi if available, (3) Close other
                  bandwidth-heavy apps on both devices.
                </p>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className={styles.conclusion}>
            <h2>Give That iPhone a New Purpose</h2>

            <p>
              That dusty iPhone in your drawer isn't obsolete—it's an
              opportunity. Instead of spending $150+ on a dedicated monitor with
              inferior cameras and questionable security, you can set up a
              professional-grade baby monitoring system in under five minutes.
            </p>

            <ul className={styles.benefitsList}>
              <li>
                <CheckCircle2 size={20} />
                Better video quality than most commercial monitors
              </li>
              <li>
                <CheckCircle2 size={20} />
                Industry-level encryption (DTLS/SRTP)
              </li>
              <li>
                <CheckCircle2 size={20} />
                Zero monthly fees—forever
              </li>
              <li>
                <CheckCircle2 size={20} />
                Monitor from anywhere in the world
              </li>
              <li>
                <CheckCircle2 size={20} />
                Reduce e-waste by repurposing old devices
              </li>
            </ul>

            <p className={styles.closingStatement}>
              Your baby deserves to be watched over. You deserve peace of mind.
              And your iPhone deserves a second life.
            </p>
          </section>
        </div>

        {/* Author Bio */}
        <div className={styles.authorBio}>
          <div className={styles.bioContent}>
            <h3>About the Author</h3>
            <p>
              <strong>Nakul Rathore</strong> is the founder of GoodBaby and a
              dad who was tired of overpriced baby monitors with terrible
              security. He built GoodBaby to give every parent access to
              professional-grade monitoring without the premium price tag.
            </p>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready for Peace of Mind?</h2>
          <p>
            Set up your free baby monitor in under 5 minutes. No credit card, no
            subscriptions, ever.
          </p>
          <Link to="/signup" className={styles.ctaButton}>
            <span>Start Monitoring Free</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} GoodBaby. All rights reserved.
          </p>
          <div className={styles.footerLinks}>
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
